import { useMemo } from "react";
import { UseQueryResult, UseMutationResult } from "@tanstack/react-query";
import {
  AgreementLoadingState,
  AgreementLoadingReason,
} from "../context/AgreementContext";
import { ReadAgreementDetail } from "@/schemas/agreement";
import { ReadAgreement } from "@/types/agreement";
import { ApiError } from "@/services/api";
import { AgreementCommentPayloadItem } from "@/services/agreements";

export interface MutationsObject {
  sendQuestionnaireMutation: UseMutationResult<
    ReadAgreement,
    ApiError,
    undefined,
    unknown
  >;
  rejectAgreementMutation: UseMutationResult<
    ReadAgreement,
    ApiError,
    undefined,
    unknown
  >;
  confirmAgreementMutation: UseMutationResult<
    ReadAgreement,
    ApiError,
    undefined,
    unknown
  >;
  commentAgreementMutation: UseMutationResult<
    ReadAgreement,
    ApiError,
    AgreementCommentPayloadItem[],
    unknown
  >;
  completeAgreementMutation: UseMutationResult<
    ReadAgreement,
    ApiError,
    { end_date: string },
    unknown
  >;
  updateContentMutation: UseMutationResult<
    ReadAgreement,
    ApiError,
    { amendment_date: string },
    unknown
  >;
  updateDataMutation: UseMutationResult<
    ReadAgreement,
    ApiError,
    { amendment_date: string },
    unknown
  >;
  updateStatementsMutation: UseMutationResult<
    ReadAgreement,
    ApiError,
    undefined,
    unknown
  >;
  updateTaxStatusMutation: UseMutationResult<
    ReadAgreement,
    ApiError,
    { amendment_date: string },
    unknown
  >;
  deleteAgreementMutation: UseMutationResult<
    unknown,
    ApiError,
    undefined,
    unknown
  >;
}

export interface QueriesObject {
  agreementQuery: UseQueryResult<ReadAgreementDetail, Error>;
}

export const useAgreementLoading = (
  queries: QueriesObject,
  mutations: MutationsObject,
): AgreementLoadingState => {
  const { isFetching, isLoading } = queries.agreementQuery;

  return useMemo((): AgreementLoadingState => {
    const loadingStates: {
      condition: boolean;
      reason: AgreementLoadingReason;
    }[] = [
      { condition: isFetching || isLoading, reason: "fetching_agreement" },
      {
        condition: mutations.sendQuestionnaireMutation.isPending,
        reason: "sending_questionnaire",
      },
      {
        condition: mutations.rejectAgreementMutation.isPending,
        reason: "rejecting_agreement",
      },
      {
        condition: mutations.confirmAgreementMutation.isPending,
        reason: "confirming_agreement",
      },
      {
        condition: mutations.commentAgreementMutation.isPending,
        reason: "requesting_comments",
      },
      {
        condition: mutations.completeAgreementMutation.isPending,
        reason: "completing_agreement",
      },
      {
        condition: mutations.updateContentMutation.isPending,
        reason: "updating_content",
      },
      {
        condition: mutations.updateDataMutation.isPending,
        reason: "updating_data",
      },
      {
        condition: mutations.updateStatementsMutation.isPending,
        reason: "updating_statements",
      },
      {
        condition: mutations.updateTaxStatusMutation.isPending,
        reason: "updating_tax_status",
      },
      {
        condition: mutations.deleteAgreementMutation.isPending,
        reason: "deleting_agreement",
      },
    ];

    const activeState = loadingStates.find((state) => state.condition);
    return activeState
      ? { isLoading: true, reason: activeState.reason }
      : { isLoading: false };
  }, [
    isFetching,
    isLoading,
    mutations.sendQuestionnaireMutation.isPending,
    mutations.rejectAgreementMutation.isPending,
    mutations.confirmAgreementMutation.isPending,
    mutations.commentAgreementMutation.isPending,
    mutations.completeAgreementMutation.isPending,
    mutations.updateContentMutation.isPending,
    mutations.updateDataMutation.isPending,
    mutations.updateStatementsMutation.isPending,
    mutations.updateTaxStatusMutation.isPending,
    mutations.deleteAgreementMutation.isPending,
  ]);
};
